import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import {
  getEstablishmentFieldRequests,
  EstablishmentFieldRequestsListResponse,
  GetEstablishmentFieldRequestsProps,
} from "@/services/establishment-field-request.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { Dispatch, SetStateAction } from "react";
import { FieldValues, UseFormGetValues, UseFormSetValue } from "react-hook-form";
import { NavigateFunction } from "react-router-dom";
import {
  EstablishmentFieldRequestFilterForm,
  getEstablishmentFieldRequestsFilter,
} from "./filter-obj";

interface FetchEstablishmentFieldRequestsHandlerProps {
  filters?: Record<string, unknown>;
  showLoad?: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;
  setList: Dispatch<SetStateAction<EstablishmentFieldRequestsListResponse | undefined>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchEstablishmentFieldRequestsHandler = async ({
  filters,
  showLoad = true,
  setLoading,
  setList,
  navigate,
  fetchPermissions,
}: FetchEstablishmentFieldRequestsHandlerProps) => {
  try {
    if (showLoad) setLoading(true);

    const response = await getEstablishmentFieldRequests({
      ...(filters as any),
      "P.Pagina": (filters as any)?.["P.Pagina"] ?? 1,
      "P.Records": 10,
    });

    setList(response ?? undefined);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.ESTABLISMENT_FIELD_REQUESTS,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error,
          "No se pudieron obtener las solicitudes de establecimientos y lotes.",
        ),
      });
    }
  } finally {
    if (showLoad) setLoading(false);
  }
};

interface SubmitFiltersHandlerProps {
  event: FieldValues;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<Partial<GetEstablishmentFieldRequestsProps> | undefined>>;
  fetchList: (filters?: any) => Promise<void>;
}

export const submitEstablishmentFieldRequestFiltersHandler = async ({
  event,
  setOpenFilters,
  setFiltersApplied,
  setCurrentFilters,
  fetchList,
}: SubmitFiltersHandlerProps) => {
  setFiltersApplied(true);

  const filterObj = getEstablishmentFieldRequestsFilter(
    event as EstablishmentFieldRequestFilterForm,
  );

  setCurrentFilters(filterObj);

  if (!Object.keys(filterObj).length) setFiltersApplied(false);

  await fetchList(filterObj);

  setOpenFilters(false);
};

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => Promise<void>;
}

export const clearEstablishmentFieldRequestFiltersHandler = async ({
  setValue,
  getValues,
  setFiltersApplied,
  setOpenFilters,
  submitFilters,
}: ClearFiltersHandlerProps) => {
  setValue("fecha", undefined);
  setValue("account_number", undefined);
  setValue("tipo_operacion", undefined);
  setValue("estados", []);

  submitFilters(getValues());
  setFiltersApplied(false);
  setOpenFilters(false);
};
